// ../shared/dist/index.js
var API_VERSION = "v1";
var API_PREFIX = `/api/${API_VERSION}`;
var MILESTONE_CATEGORIES = [
  "llmo",
  "seo",
  "ad",
  "campaign",
  "site",
  "crm",
  "other"
];

// src/index.ts
var ShopifyHarnessApiError = class extends Error {
  status;
  code;
  requestId;
  constructor(input) {
    super(input.message);
    this.name = "ShopifyHarnessApiError";
    this.status = input.status;
    this.code = input.code;
    this.requestId = input.requestId;
  }
};
var ShopifyHarnessProtocolError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "ShopifyHarnessProtocolError";
  }
};
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function requireString(value, field) {
  if (typeof value !== "string") {
    throw new ShopifyHarnessProtocolError(`Expected ${field} to be a string`);
  }
  return value;
}
function requireNullableString(value, field) {
  if (value === null) return null;
  return requireString(value, field);
}
function decodeStatus(value) {
  if (value === "active" || value === "unsubscribed" || value === "suppressed") {
    return value;
  }
  throw new ShopifyHarnessProtocolError("Expected contact.status to be valid");
}
function decodeContact(value) {
  if (!isRecord(value)) {
    throw new ShopifyHarnessProtocolError("Expected a contact object");
  }
  return {
    id: requireString(value.id, "contact.id"),
    shopDomain: requireString(value.shopDomain, "contact.shopDomain"),
    email: requireString(value.email, "contact.email"),
    firstName: requireNullableString(value.firstName, "contact.firstName"),
    lastName: requireNullableString(value.lastName, "contact.lastName"),
    status: decodeStatus(value.status),
    ordersCount: requireNullableNumber(value.ordersCount, "contact.ordersCount"),
    amountSpent: requireNullableNumber(value.amountSpent, "contact.amountSpent"),
    amountSpentCurrency: requireNullableString(
      value.amountSpentCurrency,
      "contact.amountSpentCurrency"
    ),
    ltvSyncedAt: requireNullableString(value.ltvSyncedAt, "contact.ltvSyncedAt"),
    createdAt: requireString(value.createdAt, "contact.createdAt"),
    updatedAt: requireString(value.updatedAt, "contact.updatedAt")
  };
}
function requireStringArray(value, field) {
  if (!Array.isArray(value)) {
    throw new ShopifyHarnessProtocolError(`Expected ${field} to be an array of strings`);
  }
  return value.map((entry, index) => requireString(entry, `${field}[${index}]`));
}
function requireNullableNumber(value, field) {
  if (value === null) return null;
  if (typeof value !== "number") {
    throw new ShopifyHarnessProtocolError(`Expected ${field} to be a number`);
  }
  return value;
}
function decodePersonaStatus(value) {
  if (value === "draft" || value === "active" || value === "archived") {
    return value;
  }
  throw new ShopifyHarnessProtocolError("Expected persona.status to be valid");
}
function decodePersona(value) {
  if (!isRecord(value)) {
    throw new ShopifyHarnessProtocolError("Expected a persona object");
  }
  return {
    id: requireString(value.id, "persona.id"),
    shopDomain: requireString(value.shopDomain, "persona.shopDomain"),
    name: requireString(value.name, "persona.name"),
    tagline: requireString(value.tagline, "persona.tagline"),
    summary: requireString(value.summary, "persona.summary"),
    segment: requireNullableString(value.segment, "persona.segment"),
    quote: requireNullableString(value.quote, "persona.quote"),
    demographics: requireStringArray(value.demographics, "persona.demographics"),
    goals: requireStringArray(value.goals, "persona.goals"),
    painPoints: requireStringArray(value.painPoints, "persona.painPoints"),
    behaviors: requireStringArray(value.behaviors, "persona.behaviors"),
    preferredChannels: requireStringArray(value.preferredChannels, "persona.preferredChannels"),
    evidence: requireStringArray(value.evidence, "persona.evidence"),
    contactCount: requireNullableNumber(value.contactCount, "persona.contactCount"),
    revenueShare: requireNullableNumber(value.revenueShare, "persona.revenueShare"),
    recommendation: requireNullableString(value.recommendation, "persona.recommendation"),
    recommendedActions: requireStringArray(value.recommendedActions, "persona.recommendedActions"),
    status: decodePersonaStatus(value.status),
    generatedBy: requireNullableString(value.generatedBy, "persona.generatedBy"),
    createdAt: requireString(value.createdAt, "persona.createdAt"),
    updatedAt: requireString(value.updatedAt, "persona.updatedAt")
  };
}
function decodePersonaList(value) {
  if (!isRecord(value) || !Array.isArray(value.personas)) {
    throw new ShopifyHarnessProtocolError("Received an invalid persona list response");
  }
  return {
    personas: value.personas.map(decodePersona),
    cursor: requireNullableString(value.cursor, "cursor")
  };
}
function decodePersonaDelete(value) {
  if (!isRecord(value) || value.deleted !== true) {
    throw new ShopifyHarnessProtocolError("Received an invalid persona delete response");
  }
  return {
    deleted: true,
    id: requireString(value.id, "id")
  };
}
function requireNumber(value, field) {
  if (typeof value !== "number") {
    throw new ShopifyHarnessProtocolError(`Expected ${field} to be a number`);
  }
  return value;
}
function decodeSegment(value) {
  if (!isRecord(value)) {
    throw new ShopifyHarnessProtocolError("Expected a segment object");
  }
  return {
    id: requireString(value.id, "segment.id"),
    shopDomain: requireString(value.shopDomain, "segment.shopDomain"),
    name: requireString(value.name, "segment.name"),
    description: requireNullableString(value.description, "segment.description"),
    memberCount: requireNumber(value.memberCount, "segment.memberCount"),
    createdAt: requireString(value.createdAt, "segment.createdAt"),
    updatedAt: requireString(value.updatedAt, "segment.updatedAt")
  };
}
function decodeSegmentList(value) {
  if (!isRecord(value) || !Array.isArray(value.segments)) {
    throw new ShopifyHarnessProtocolError("Received an invalid segment list response");
  }
  return {
    segments: value.segments.map(decodeSegment),
    cursor: requireNullableString(value.cursor, "cursor")
  };
}
function decodeSegmentAssignment(value) {
  if (!isRecord(value)) {
    throw new ShopifyHarnessProtocolError("Received an invalid segment assignment response");
  }
  return {
    segmentId: requireString(value.segmentId, "segmentId"),
    assigned: requireNumber(value.assigned, "assigned"),
    missingEmails: requireStringArray(value.missingEmails, "missingEmails")
  };
}
function decodeScenarioStatus(value) {
  if (value === "draft" || value === "active" || value === "paused") {
    return value;
  }
  throw new ShopifyHarnessProtocolError("Expected scenario.status to be valid");
}
function decodeScenario(value) {
  if (!isRecord(value)) {
    throw new ShopifyHarnessProtocolError("Expected a scenario object");
  }
  return {
    id: requireString(value.id, "scenario.id"),
    shopDomain: requireString(value.shopDomain, "scenario.shopDomain"),
    name: requireString(value.name, "scenario.name"),
    description: requireNullableString(value.description, "scenario.description"),
    trigger: requireString(value.trigger, "scenario.trigger"),
    condition: requireNullableString(value.condition, "scenario.condition"),
    actions: requireStringArray(value.actions, "scenario.actions"),
    status: decodeScenarioStatus(value.status),
    createdAt: requireString(value.createdAt, "scenario.createdAt"),
    updatedAt: requireString(value.updatedAt, "scenario.updatedAt")
  };
}
function decodeScenarioList(value) {
  if (!isRecord(value) || !Array.isArray(value.scenarios)) {
    throw new ShopifyHarnessProtocolError("Received an invalid scenario list response");
  }
  return {
    scenarios: value.scenarios.map(decodeScenario),
    cursor: requireNullableString(value.cursor, "cursor")
  };
}
function decodeMilestoneCategory(value) {
  if (typeof value === "string" && MILESTONE_CATEGORIES.includes(value)) {
    return value;
  }
  throw new ShopifyHarnessProtocolError("Expected milestone.category to be valid");
}
function decodeMilestone(value) {
  if (!isRecord(value)) {
    throw new ShopifyHarnessProtocolError("Expected a milestone object");
  }
  return {
    id: requireString(value.id, "milestone.id"),
    shopDomain: requireString(value.shopDomain, "milestone.shopDomain"),
    startedOn: requireString(value.startedOn, "milestone.startedOn"),
    endedOn: requireNullableString(value.endedOn, "milestone.endedOn"),
    category: decodeMilestoneCategory(value.category),
    title: requireString(value.title, "milestone.title"),
    description: requireNullableString(value.description, "milestone.description"),
    createdAt: requireString(value.createdAt, "milestone.createdAt"),
    updatedAt: requireString(value.updatedAt, "milestone.updatedAt")
  };
}
function decodeMilestoneList(value) {
  if (!isRecord(value) || !Array.isArray(value.milestones)) {
    throw new ShopifyHarnessProtocolError("Received an invalid milestone list response");
  }
  return {
    milestones: value.milestones.map(decodeMilestone),
    cursor: requireNullableString(value.cursor, "cursor")
  };
}
function decodeMilestoneDelete(value) {
  if (!isRecord(value) || value.deleted !== true) {
    throw new ShopifyHarnessProtocolError("Received an invalid milestone delete response");
  }
  return {
    deleted: true,
    id: requireString(value.id, "id")
  };
}
function decodeHealth(value) {
  if (!isRecord(value) || value.status !== "ok" || value.service !== "shopify-harness-worker") {
    throw new ShopifyHarnessProtocolError("Received an invalid health response");
  }
  return {
    service: value.service,
    status: value.status,
    timestamp: requireString(value.timestamp, "health.timestamp"),
    version: requireString(value.version, "health.version")
  };
}
function decodeContactList(value) {
  if (!isRecord(value) || !Array.isArray(value.contacts)) {
    throw new ShopifyHarnessProtocolError("Received an invalid contact list response");
  }
  return {
    contacts: value.contacts.map(decodeContact),
    cursor: requireNullableString(value.cursor, "cursor")
  };
}
function requireBoolean(value, field) {
  if (typeof value !== "boolean") {
    throw new ShopifyHarnessProtocolError(`Expected ${field} to be a boolean`);
  }
  return value;
}
function decodeLtvSync(value) {
  if (!isRecord(value)) {
    throw new ShopifyHarnessProtocolError("Received an invalid LTV sync response");
  }
  return {
    shopDomain: requireString(value.shopDomain, "shopDomain"),
    synced: requireNumber(value.synced, "synced"),
    skipped: requireNumber(value.skipped, "skipped"),
    truncated: requireBoolean(value.truncated, "truncated"),
    startedAt: requireString(value.startedAt, "startedAt"),
    completedAt: requireString(value.completedAt, "completedAt")
  };
}
function decodeApiError(value) {
  if (!isRecord(value) || !isRecord(value.error)) return null;
  if (typeof value.error.code !== "string" || typeof value.error.message !== "string") return null;
  const requestId = typeof value.error.requestId === "string" ? value.error.requestId : void 0;
  return {
    error: {
      code: value.error.code,
      message: value.error.message,
      ...requestId ? { requestId } : {}
    }
  };
}
async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    throw new ShopifyHarnessProtocolError(
      `Expected JSON from Shopify Harness API (${response.status})`
    );
  }
}
var ShopifyHarnessClient = class {
  contacts;
  personas;
  segments;
  scenarios;
  milestones;
  #baseUrl;
  #accessToken;
  #fetch;
  #headers;
  constructor(options) {
    this.#baseUrl = options.baseUrl.replace(/\/$/, "");
    this.#accessToken = options.accessToken;
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.#headers = options.headers ?? {};
    this.contacts = {
      list: (listOptions) => this.listContacts(listOptions),
      syncLtv: (input, requestOptions) => this.syncContactLtv(input, requestOptions)
    };
    this.personas = {
      list: (listOptions) => this.listPersonas(listOptions),
      get: (personaId, requestOptions) => this.getPersona(personaId, requestOptions),
      upsert: (input, requestOptions) => this.upsertPersona(input, requestOptions),
      delete: (personaId, requestOptions) => this.deletePersona(personaId, requestOptions)
    };
    this.segments = {
      list: (listOptions) => this.listSegments(listOptions),
      upsert: (input, requestOptions) => this.upsertSegment(input, requestOptions),
      assignContacts: (segmentId, emails, requestOptions) => this.assignSegmentContacts(segmentId, emails, requestOptions)
    };
    this.scenarios = {
      list: (listOptions) => this.listScenarios(listOptions),
      upsert: (input, requestOptions) => this.upsertScenario(input, requestOptions)
    };
    this.milestones = {
      list: (listOptions) => this.listMilestones(listOptions),
      create: (input, requestOptions) => this.createMilestone(input, requestOptions),
      update: (milestoneId, patch, requestOptions) => this.updateMilestone(milestoneId, patch, requestOptions),
      delete: (milestoneId, requestOptions) => this.deleteMilestone(milestoneId, requestOptions)
    };
  }
  async health(signal) {
    return this.request(`${API_PREFIX}/health`, decodeHealth, signal ? { signal } : {});
  }
  async listContacts(options = {}) {
    const query = new URLSearchParams();
    if (options.cursor) query.set("cursor", options.cursor);
    if (options.limit !== void 0) query.set("limit", String(options.limit));
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return this.request(
      `${API_PREFIX}/contacts${suffix}`,
      decodeContactList,
      options.signal ? { signal: options.signal } : {}
    );
  }
  async syncContactLtv(input = {}, options = {}) {
    return this.request(`${API_PREFIX}/contacts/ltv-sync`, decodeLtvSync, {
      method: "POST",
      body: input.shopDomain ? { shopDomain: input.shopDomain } : {},
      ...options.signal ? { signal: options.signal } : {}
    });
  }
  async listPersonas(options = {}) {
    const query = new URLSearchParams();
    if (options.cursor) query.set("cursor", options.cursor);
    if (options.limit !== void 0) query.set("limit", String(options.limit));
    if (options.status) query.set("status", options.status);
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return this.request(
      `${API_PREFIX}/personas${suffix}`,
      decodePersonaList,
      options.signal ? { signal: options.signal } : {}
    );
  }
  async getPersona(personaId, options = {}) {
    return this.request(
      `${API_PREFIX}/personas/${encodeURIComponent(personaId)}`,
      decodePersona,
      options.signal ? { signal: options.signal } : {}
    );
  }
  async upsertPersona(input, options = {}) {
    return this.request(`${API_PREFIX}/personas`, decodePersona, {
      method: "POST",
      body: input,
      ...options.signal ? { signal: options.signal } : {}
    });
  }
  async deletePersona(personaId, options = {}) {
    return this.request(
      `${API_PREFIX}/personas/${encodeURIComponent(personaId)}`,
      decodePersonaDelete,
      {
        method: "DELETE",
        ...options.signal ? { signal: options.signal } : {}
      }
    );
  }
  async listSegments(options = {}) {
    const query = new URLSearchParams();
    if (options.cursor) query.set("cursor", options.cursor);
    if (options.limit !== void 0) query.set("limit", String(options.limit));
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return this.request(
      `${API_PREFIX}/segments${suffix}`,
      decodeSegmentList,
      options.signal ? { signal: options.signal } : {}
    );
  }
  async upsertSegment(input, options = {}) {
    return this.request(`${API_PREFIX}/segments`, decodeSegment, {
      method: "POST",
      body: input,
      ...options.signal ? { signal: options.signal } : {}
    });
  }
  async assignSegmentContacts(segmentId, emails, options = {}) {
    return this.request(
      `${API_PREFIX}/segments/${encodeURIComponent(segmentId)}/contacts`,
      decodeSegmentAssignment,
      {
        method: "POST",
        body: { emails },
        ...options.signal ? { signal: options.signal } : {}
      }
    );
  }
  async listScenarios(options = {}) {
    const query = new URLSearchParams();
    if (options.cursor) query.set("cursor", options.cursor);
    if (options.limit !== void 0) query.set("limit", String(options.limit));
    if (options.status) query.set("status", options.status);
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return this.request(
      `${API_PREFIX}/scenarios${suffix}`,
      decodeScenarioList,
      options.signal ? { signal: options.signal } : {}
    );
  }
  async upsertScenario(input, options = {}) {
    return this.request(`${API_PREFIX}/scenarios`, decodeScenario, {
      method: "POST",
      body: input,
      ...options.signal ? { signal: options.signal } : {}
    });
  }
  async listMilestones(options = {}) {
    const query = new URLSearchParams();
    if (options.cursor) query.set("cursor", options.cursor);
    if (options.limit !== void 0) query.set("limit", String(options.limit));
    if (options.category) query.set("category", options.category);
    if (options.from) query.set("from", options.from);
    if (options.to) query.set("to", options.to);
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return this.request(
      `${API_PREFIX}/milestones${suffix}`,
      decodeMilestoneList,
      options.signal ? { signal: options.signal } : {}
    );
  }
  async createMilestone(input, options = {}) {
    return this.request(`${API_PREFIX}/milestones`, decodeMilestone, {
      method: "POST",
      body: input,
      ...options.signal ? { signal: options.signal } : {}
    });
  }
  async updateMilestone(milestoneId, patch, options = {}) {
    return this.request(
      `${API_PREFIX}/milestones/${encodeURIComponent(milestoneId)}`,
      decodeMilestone,
      {
        method: "PUT",
        body: patch,
        ...options.signal ? { signal: options.signal } : {}
      }
    );
  }
  async deleteMilestone(milestoneId, options = {}) {
    return this.request(
      `${API_PREFIX}/milestones/${encodeURIComponent(milestoneId)}`,
      decodeMilestoneDelete,
      {
        method: "DELETE",
        ...options.signal ? { signal: options.signal } : {}
      }
    );
  }
  async request(path, decode, options = {}) {
    const hasBody = options.body !== void 0;
    const response = await this.#fetch(`${this.#baseUrl}${path}`, {
      method: options.method ?? "GET",
      headers: {
        accept: "application/json",
        ...hasBody ? { "content-type": "application/json" } : {},
        ...this.#headers,
        ...this.#accessToken ? { authorization: `Bearer ${this.#accessToken}` } : {}
      },
      ...hasBody ? { body: JSON.stringify(options.body) } : {},
      ...options.signal ? { signal: options.signal } : {}
    });
    const body = await parseJson(response);
    if (!response.ok) {
      const apiError = decodeApiError(body);
      throw new ShopifyHarnessApiError({
        status: response.status,
        code: apiError?.error.code ?? "http_error",
        message: apiError?.error.message ?? `Request failed with status ${response.status}`,
        ...apiError?.error.requestId ? { requestId: apiError.error.requestId } : {}
      });
    }
    return decode(body);
  }
};
function createShopifyHarnessClient(options) {
  return new ShopifyHarnessClient(options);
}
export {
  MILESTONE_CATEGORIES,
  ShopifyHarnessApiError,
  ShopifyHarnessClient,
  ShopifyHarnessProtocolError,
  createShopifyHarnessClient
};
//# sourceMappingURL=index.js.map